import {
  createVolumeHeaders,
  volumeApiFetch,
  VOLUME_API_VERSION_HEADERS,
  formatDateTimeForApi,
  withApplicationId,
} from "@/lib/volume-api-utils";
import { z } from "zod/v4";

const PayerAccountIdentifierSchema = z
  .object({
    type: z.string().nullable(),
    accountNumber: z.string().nullable(),
    sortCode: z.string().nullable(),
    iban: z.string().nullable(),
    bic: z.string().nullable(),
  })
  .nullable();

const PayerAddressSchema = z
  .object({
    line1: z.string().nullable(),
    line2: z.string().nullable(),
    country: z.string().nullable(),
    town: z.string().nullable(),
    postCode: z.string().nullable(),
  })
  .nullable();

const BasePaymentSchema = z.object({
  paymentId: z.string(),
  merchantPaymentId: z.string().nullable(),
  paymentStatus: z.enum([
    "NEW",
    "AWAITING_AUTHORIZATION",
    "AUTHORIZED",
    "PENDING",
    "COMPLETED",
    "FAILED",
    "SETTLED",
    "EXPIRED",
  ]),
  paymentType: z.enum(["VOLUME", "EXTERNAL"]),
  isNewPayment: z.boolean(),
  newPaymentId: z.string().nullable(),
  newAttemptId: z.string().nullable(),
  tracingId: z.string(),
  createdAt: z.string(),
  isToVirtualAccount: z.boolean(),
  canStartRefund: z.boolean(),
  amountRefunded: z.number(),
  amountRefunding: z.number(),
  refunds: z.array(
    z.object({
      refundId: z.string(),
      createdAtUtc: z.string(),
      updatedAtUtc: z.string(),
      refundPaymentReference: z.string(),
      refundPaymentAmount: z.number(),
      refundPaymentCurrency: z.string(),
      refundStatus: z.string(),
    }),
  ),
  payer: z
    .object({
      name: z.string().nullable(),
      email: z.string().nullable(),
      accountIdentifier: PayerAccountIdentifierSchema,
      address: PayerAddressSchema,
      payerIP: z.string().nullable(),
    })
    .nullable(),
  statusTags: z.array(z.string()).default([]),
  riskCategory: z.enum(["LOW", "MEDIUM", "HIGH", "NA"]).nullable(),
  merchantReference: z.string().nullable(),
  merchantProvidedPayerDetailsNames: z.string().nullable(),
});

const EURPaymentSchema = BasePaymentSchema.extend({
  paymentRequest: z.object({
    amount: z.number(),
    currency: z.literal("EUR"),
    reference: z.string().nullable(),
  }),
});

const GBPPaymentSchema = BasePaymentSchema.extend({
  paymentRequest: z.object({
    amount: z.number(),
    currency: z.literal("GBP"),
    reference: z.string().nullable(),
  }),
});

const PaymentSchema = z.union([EURPaymentSchema, GBPPaymentSchema]);

const PaymentsSchema = z.object({
  content: z.array(PaymentSchema),
  pageable: z.object({
    sort: z.array(
      z.object({
        direction: z.string(),
        property: z.string(),
        ignoreCase: z.boolean(),
        nullHandling: z.string(),
        ascending: z.boolean(),
        descending: z.boolean(),
      }),
    ),
    pageNumber: z.number(),
    pageSize: z.number(),
    offset: z.number(),
    paged: z.boolean(),
    unpaged: z.boolean(),
  }),
  last: z.boolean(),
  totalElements: z.number(),
  totalPages: z.number(),
  first: z.boolean(),
  number: z.number(),
  numberOfElements: z.number(),
  size: z.number(),
  empty: z.boolean(),
});
export type Payments = z.infer<typeof PaymentsSchema>;

export const getPayments = async ({
  accessToken,
  applicationId,
  params,
}: {
  accessToken: string;
  applicationId: string;
  params: {
    paymentStatuses: string | undefined;
    startDateTimeUtc: string | undefined;
    endDateTimeUtc: string | undefined;
    currencies: string | undefined;
    page: string;
    size: string;
    sort: string;
    merchantId: string | undefined;
    globalSearch: string | undefined;
    amountFrom: number | undefined;
    amountTo: number | undefined;
    paymentTypes: string | undefined;
    refundedFilter: string | undefined;
    heldFilter: string | undefined;
    riskCategory: string | undefined;
  };
}) => {
  function safeSizeValue(value: string | undefined) {
    if (value && Number(value) > 100) {
      return "10";
    }
    return value;
  }
  const queryParams = withApplicationId(
    {
      ...(params.paymentStatuses && {
        paymentStatuses: params.paymentStatuses,
      }),
      ...(params.currencies && { currencies: params.currencies }),
      page: params.page,
      size: safeSizeValue(params.size),
      ...(params.sort && { sort: params.sort }),
      ...(params.merchantId && { merchantId: params.merchantId }),
      ...(params.globalSearch && { globalSearch: params.globalSearch }),
      ...(params.paymentTypes && { paymentTypes: params.paymentTypes }),
      ...(params.refundedFilter && { refundedFilter: params.refundedFilter }),
      ...(params.heldFilter && { heldFilter: params.heldFilter }),
      ...(params.riskCategory && { riskCategory: params.riskCategory }),
      ...(params.startDateTimeUtc && {
        startDateTimeUtc: formatDateTimeForApi(params.startDateTimeUtc),
      }),
      ...(params.endDateTimeUtc && {
        endDateTimeUtc: formatDateTimeForApi(params.endDateTimeUtc),
      }),
      includeNewPayments: true,
      ...(params.amountFrom && { minAmount: params.amountFrom }),
      ...(params.amountTo && { maxAmount: params.amountTo }),
    },
    applicationId,
  );

  const responseJson = await volumeApiFetch({
    endpoint: "payments",
    method: "GET",
    queryParameters: queryParams,
    headers: createVolumeHeaders({
      accessToken,
      applicationId,
      acceptHeader: VOLUME_API_VERSION_HEADERS.v08,
    }),
  });

  return PaymentsSchema.safeParse(responseJson);
};
